// Guia de primeiros passos: aparece para o administrador de uma empresa nova até concluir ou ocultar.
// Cada passo é detectado pela API (o que já existe no banco): o cliente não marca nada à mão.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { linkSuporte, telefoneSuporte } from '../components/Marca';
import { CardTour } from '../components/Tour';
import { Carregando, Erro, Icon } from '../components/ui';

type Passo = 'empresa' | 'equipe' | 'garimpar' | 'carteira' | 'email' | 'mensagem' | 'contato';
export interface StatusPassos {
  passos: Record<Passo, boolean>; feitos: number; total: number; mostrar: boolean;
  trialAte: string | null; suporteWhatsapp: string;
}

export const usePrimeirosPassos = (habilitado = true) =>
  useQuery({ queryKey: ['primeiros-passos'], queryFn: () => api<StatusPassos>('/primeiros-passos'), enabled: habilitado, staleTime: 30_000 });

const PASSOS: Array<{ id: Passo; titulo: string; texto: string; tempo: string; acao: string; para: string }> = [
  { id: 'empresa', titulo: 'Complete os dados da empresa', texto: 'Cidade, estado e segmento: aparecem para a equipe e ajudam a garimpar perto de você.', tempo: '1 min', acao: 'Completar', para: '/configuracoes/empresa' },
  { id: 'equipe', titulo: 'Cadastre sua equipe', texto: 'Cada vendedor com login próprio e a carteira dele.', tempo: '2 min', acao: 'Cadastrar vendedores', para: '/usuarios' },
  { id: 'garimpar', titulo: 'Garimpe seus primeiros clientes', texto: 'Na Prospecção, escolha a atividade e a cidade e importe as empresas para a carteira.', tempo: '3 min', acao: 'Garimpar agora', para: '/prospeccao' },
  { id: 'carteira', titulo: 'Cadastre um cliente que você já atende', texto: 'Comece pelos clientes mais importantes da sua carteira atual.', tempo: '2 min', acao: 'Cadastrar cliente', para: '/clientes/novo' },
  { id: 'email', titulo: 'Conecte seu e-mail de envio', texto: 'Os e-mails saem da sua própria caixa, e as respostas dos clientes aparecem na ficha deles.', tempo: '2 min', acao: 'Conectar e-mail', para: '/configuracoes/email' },
  { id: 'mensagem', titulo: 'Crie suas mensagens de apresentação', texto: 'Modelos de WhatsApp e de e-mail com o nome do cliente preenchido sozinho.', tempo: '2 min', acao: 'Criar mensagens', para: '/templates' },
  { id: 'contato', titulo: 'Faça o primeiro contato e registre', texto: 'Mande um WhatsApp ou e-mail, ou registre uma ligação ou visita. Atualize o status do cliente e agende o retorno.', tempo: '1 min', acao: 'Abrir clientes', para: '/clientes' },
];

const DICAS: Record<Passo | 'fim', [string, string]> = {
  empresa: ['Comece pelo básico', 'Com a cidade da empresa preenchida, fica mais fácil garimpar clientes na sua região.'],
  equipe: ['Cada um com a sua carteira', 'Vendedores veem só os clientes deles. Você, como administrador, vê tudo e acompanha a equipe.'],
  garimpar: ['Comece pequeno e local', 'Escolha uma atividade e a sua cidade. Prefira empresas com celular: a chance de o WhatsApp ser do dono é maior.'],
  carteira: ['Sua carteira vale ouro', 'Só nome e telefone já bastam para cadastrar. O resto você completa com o tempo.'],
  email: ['Cliente que prefere e-mail', 'Na ficha do cliente dá para marcar o canal preferido. Quem prefere e-mail recebe por e-mail também nas campanhas.'],
  mensagem: ['Mensagem curta converte mais', 'Apresente-se em uma frase e termine com uma pergunta simples. Evite textão no primeiro contato.'],
  contato: ['Todo contato vira um retorno', 'Ligou ou visitou? Use Registrar contato, ajuste o status e já agende quando falar de novo.'],
  fim: ['Rotina de garimpeiro', 'Todo dia: abra Retornos, fale com quem está agendado e garimpe algumas empresas novas.'],
};

export function PrimeirosPassosPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = usePrimeirosPassos();
  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const s = q.data!;
  const proximo = PASSOS.find((p) => !s.passos[p.id]);
  const pct = Math.round((s.feitos / s.total) * 100);
  const dica = DICAS[proximo?.id ?? 'fim'];

  async function ocultar() {
    await api('/primeiros-passos/ocultar', { body: {} }).catch(() => {});
    await qc.invalidateQueries({ queryKey: ['primeiros-passos'] });
    nav('/', { replace: true });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Olá, {me!.usuario.nome.split(' ')[0]}! Vamos colocar a {me!.empresa.nome} para vender.</h1>
          <span className="muted">
            Sete passos rápidos, em qualquer ordem. Cada um é marcado sozinho quando você o conclui.
            {s.trialAte && <> Seu teste grátis vai até <b>{new Date(s.trialAte).toLocaleDateString('pt-BR')}</b>.</>}
          </span>
        </div>
        <div className="actions"><button type="button" className="btn sm" onClick={ocultar}>Ocultar guia</button></div>
      </div>

      <div className="passos-grid">
        <section className="card" aria-labelledby="h-progresso" style={{ gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 id="h-progresso">Seu progresso</h2>
              <span className="num small" style={{ color: 'var(--amber-text)' }}>{s.feitos} de {s.total} · {pct}%</span>
            </div>
            <div className="bar" role="progressbar" aria-label="Progresso dos primeiros passos" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} style={{ height: 8 }}>
              <div style={{ width: `${pct}%`, background: '#f5b83d' }} />
            </div>
          </div>
          <ol className="lista-passos">
            {PASSOS.map((p) => {
              const feito = s.passos[p.id];
              const atual = proximo?.id === p.id;
              return (
                <li key={p.id} className={`${feito ? 'feito' : ''}${atual ? ' atual' : ''}`}>
                  <span className="marca" aria-hidden="true">{feito && <Icon name="check" size={15} color="#0a0b0d" />}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span className="titulo">{p.titulo}<span className="sr-only">{feito ? ' (concluído)' : ''}</span></span>
                    <span className="small muted">{p.texto}</span>
                  </div>
                  <span className="small faint num tempo">{p.tempo}</span>
                  {feito ? <span className="small" style={{ color: 'var(--green-text)', textAlign: 'right' }}>Concluído</span>
                    : <Link to={p.para} className={`btn sm${atual ? ' ouro' : ''}`}>{p.acao}</Link>}
                </li>
              );
            })}
          </ol>
          {s.feitos === s.total && (
            <div className="alert ok"><Icon name="check" /><span>Pronto! A {me!.empresa.nome} já está garimpando. O guia sai do menu; o Dashboard mostra seus números do dia.</span></div>
          )}
        </section>

        <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <section className="card dica-garimpeiro" aria-labelledby="h-dica">
            <span className="rotulo">Dica do garimpeiro</span>
            <h2 id="h-dica">{dica[0]}</h2>
            <p style={{ margin: 0 }}>{dica[1]}</p>
          </section>
          <CardTour />
          <section className="card" aria-labelledby="h-ajuda">
            <h2 id="h-ajuda">Precisa de ajuda?</h2>
            <a className="ajuda-link" href={linkSuporte(s.suporteWhatsapp, `Olá! Estou começando no Garimpa Vendas (${me!.empresa.nome}) e tenho uma dúvida.`)} target="_blank" rel="noreferrer">
              <span className="ic" aria-hidden="true"><Icon name="whatsapp" size={18} color="#6ee7b7" /></span>
              <span style={{ display: 'flex', flexDirection: 'column' }}><span style={{ fontWeight: 500 }}>Falar com o suporte</span><span className="small faint">WhatsApp {telefoneSuporte(s.suporteWhatsapp)}</span></span>
            </a>
          </section>
        </aside>
      </div>
    </>
  );
}
